import {
  CHECKLIST_CHILDREN_MAX,
  CHECKLIST_ITEM_MAX,
  NAME_MAX,
  type ChecklistBody,
  type ChecklistDto,
} from '@iac/contracts';
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DeleteButton } from '../../components/DeleteButton';
import { Field, SelectField } from '../../components/Field';
import { LineListEditor } from '../../components/LineListEditor';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { SegmentedControl } from '../../components/SegmentedControl';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import { NotFound } from '../../routes/NotFound';
import {
  useChecklistGroups,
  useChecklists,
  useCreateChecklist,
  useDeleteChecklist,
  useUpdateChecklist,
} from './api';
import { useChecklistFromUrl } from './ChecklistView';
import { ChildPicker } from './ChildPicker';
import { checklistsDict } from './i18n';
import { parentsOf, type StandardChecklist } from './lists';

type Kind = ChecklistDto['kind'];

/**
 * What the form edits. Both kinds' drafts are kept, so switching the kind of a
 * new list and back loses nothing; only the chosen one is sent.
 */
interface ChecklistValues {
  name: string;
  kind: Kind;
  /** `null` until picked on a new list: it then goes to the first group. */
  groupId: string | null;
  /** The item rows as typed, and beside them each row's item id. */
  lines: string[];
  ids: (string | null)[];
  childIds: string[];
}

function valuesOf(list: ChecklistDto | null): ChecklistValues {
  const items = list?.kind === 'standard' ? list.items : [];
  // An empty list still shows one row, ready for typing.
  return {
    name: list?.name ?? '',
    kind: list?.kind ?? 'standard',
    groupId: list?.groupId ?? null,
    lines: items.length > 0 ? items.map((i) => i.text) : [''],
    ids: items.length > 0 ? items.map((i) => i.id) : [null],
    childIds: list?.kind === 'combined' ? [...list.childIds] : [],
  };
}

/**
 * A kept row carries its item's id, so the server keeps that item's tick. No
 * group lets the server place it — in the default group, for a user who has
 * none yet.
 */
function bodyOf(
  values: ChecklistValues,
  groupId: string | undefined,
): ChecklistBody {
  return values.kind === 'standard'
    ? {
        kind: 'standard',
        name: values.name,
        groupId,
        items: values.lines.map((text, i) => ({
          id: values.ids[i] ?? undefined,
          text,
        })),
      }
    : {
        kind: 'combined',
        name: values.name,
        groupId,
        childIds: values.childIds,
      };
}

export function ChecklistNew() {
  const create = useCreateChecklist();
  const navigate = useNavigate();
  return (
    <ChecklistForm
      list={null}
      pending={create.isPending}
      cancelTo="/checklists"
      onSubmit={(body) =>
        create.mutate(body, {
          onSuccess: (list) => void navigate(`/checklists/${list.id}`),
        })
      }
    />
  );
}

export function ChecklistEdit() {
  const found = useChecklistFromUrl();
  const update = useUpdateChecklist();
  const navigate = useNavigate();
  if (found === undefined) return null;
  if (found === null) return <NotFound />;
  const { list } = found;
  return (
    <ChecklistForm
      key={list.id}
      list={list}
      pending={update.isPending}
      cancelTo={`/checklists/${list.id}`}
      onSubmit={(body) =>
        update.mutate(
          { id: list.id, ...body },
          { onSuccess: () => void navigate(`/checklists/${list.id}`) },
        )
      }
    />
  );
}

/**
 * One form for both pages. A failed save — a duplicate name, say — leaves
 * everything typed in place; the reason is toasted.
 */
function ChecklistForm({
  list,
  pending,
  cancelTo,
  onSubmit,
}: {
  list: ChecklistDto | null;
  pending: boolean;
  cancelTo: string;
  onSubmit: (body: ChecklistBody) => void;
}) {
  const t = useDict(checklistsDict);
  const title = list ? t.editChecklist : t.newChecklist;
  usePageTitle(`${title} — ${t.titleSuffix}`);
  const formId = useId();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { data: lists } = useChecklists();
  const { data: groups } = useChecklistGroups();
  const remove = useDeleteChecklist();
  const [values, setValues] = useState(() => valuesOf(list));
  const set = <K extends keyof ChecklistValues>(
    key: K,
    value: ChecklistValues[K],
  ) => setValues((v) => ({ ...v, [key]: value }));

  const collator = useMemo(() => new Intl.Collator(locale), [locale]);
  const standardLists = useMemo(
    () =>
      (lists ?? [])
        .filter((l): l is StandardChecklist => l.kind === 'standard')
        .sort((a, b) => collator.compare(a.name, b.name)),
    [lists, collator],
  );
  const parentNames = list
    ? parentsOf(list, lists ?? [], collator).map((p) => p.name)
    : [];

  // A new list starts in the first group, once the groups have loaded.
  const groupId = values.groupId ?? groups?.[0]?.id;

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit(bodyOf(values, groupId));
  }

  const kindLabel = (kind: Kind) =>
    kind === 'combined' ? t.combined : t.standard;
  const canSave =
    !pending &&
    values.name.trim() !== '' &&
    (values.kind === 'standard' || values.childIds.length > 0);

  return (
    <>
      <PageHeader
        title={title}
        actions={
          list && (
            <DeleteButton
              label={t.deleteChecklist}
              confirm={
                parentNames.length > 0
                  ? t.confirmDeleteChild(list.name, parentNames)
                  : t.confirmDelete(list.name)
              }
              onConfirm={() => {
                // Leave first: the edit page of a deleted list has nothing to
                // show. A failure is toasted on the overview.
                remove.mutate(list.id);
                void navigate('/checklists');
              }}
            />
          )
        }
      />
      <Panel>
        <form onSubmit={submit} className="form-stack">
          <Field
            id={`${formId}-name`}
            label={t.name}
            required
            maxLength={NAME_MAX}
            autoComplete="off"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
          />
          {groups && groups.length > 0 && (
            <SelectField
              id={`${formId}-group`}
              label={t.group}
              value={groupId}
              onChange={(e) => set('groupId', e.target.value)}
            >
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </SelectField>
          )}
          {list ? (
            <div>
              <p className="field-label">{t.kind}</p>
              <p>{kindLabel(list.kind)}</p>
            </div>
          ) : (
            <div>
              <p className="field-label" id={`${formId}-kind`}>
                {t.kind}
              </p>
              <SegmentedControl
                label={t.kind}
                options={[
                  { value: 'standard', label: t.standard },
                  { value: 'combined', label: t.combined },
                ]}
                value={values.kind}
                onChange={(kind) => set('kind', kind)}
              />
            </div>
          )}
          {values.kind === 'standard' ? (
            <LineListEditor
              label={t.items}
              value={values.lines}
              ids={values.ids}
              onChange={(lines, ids) =>
                setValues((v) => ({ ...v, lines, ids }))
              }
              maxLength={CHECKLIST_ITEM_MAX}
              labels={{
                item: (i) => t.item(i + 1),
                moveUp: t.moveUp,
                moveDown: t.moveDown,
                remove: t.removeItem,
                add: t.addItem,
              }}
            />
          ) : (
            <ChildPicker
              label={t.children}
              emptyText={t.noStandardLists}
              options={standardLists}
              value={values.childIds}
              onChange={(ids) => set('childIds', ids)}
              labels={{
                moveUp: t.moveUp,
                moveDown: t.moveDown,
                remove: t.removeItem,
                add: t.addChild,
                addHeading: t.addChildHeading,
              }}
              max={CHECKLIST_CHILDREN_MAX}
            />
          )}
          <div className="form-actions">
            <Link to={cancelTo} className="btn-ghost">
              {t.cancel}
            </Link>
            <button
              type="submit"
              className="btn-primary checklist-form-save"
              disabled={!canSave}
            >
              {pending ? t.saving : t.save}
            </button>
          </div>
        </form>
      </Panel>
    </>
  );
}
